import type { LinkedAccounts, ProviderLabel, SuccessCode } from 'bundles/third-party-auth/components/AccountLink/types';
import {
  getLinkedOAuthProvider,
  isSsoLinked,
} from 'bundles/third-party-auth/components/AccountLink/utils/linkedAccountStatus';
import { USER_PASSWORD_MAX_LENGTH, USER_PASSWORD_MIN_LENGTH } from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/third-party-auth';

export type { ProviderLabel, SuccessCode };

export const getErrorMessage = (errorCode: string, provider: ProviderLabel, isLinked: boolean): string | null => {
  if (errorCode === 'accountIsAlreadyLinked') {
    return _t('This #{provider} account has already been linked to another Coursera account.', { provider });
  }

  if (errorCode === 'accountNotLinked') {
    return _t('Your Coursera account is not currently linked to any #{provider} accounts.', { provider });
  }

  if (errorCode === 'noPasswordSupplied') {
    return null;
  }

  if (isLinked && errorCode === 'Password is not sufficiently complex') {
    return _t(
      'Password must be between #{minLength} and #{maxLength} characters. For your security, avoid passwords like qwerty123 or password123.',
      {
        minLength: USER_PASSWORD_MIN_LENGTH,
        maxLength: USER_PASSWORD_MAX_LENGTH,
      }
    );
  }

  if (isLinked) {
    return _t("Uh oh! We weren't able to unlink your account. Please try again later.");
  }

  return _t("Uh oh! We weren't able to link your account. Please try again later.");
};

export const getSuccessMessage = (successCode: SuccessCode, provider: ProviderLabel): string | null => {
  if (successCode === 'link') {
    return _t('Successfully linked your #{provider} account.', { provider });
  }

  if (successCode === 'delink') {
    return _t('Successfully unlinked your #{provider} account.', { provider });
  }

  return null;
};

export const getOAuthDisabledMessage = (linked: LinkedAccounts): string | null => {
  if (isSsoLinked(linked)) {
    return null;
  }
  const provider = getLinkedOAuthProvider(linked);
  return provider ? _t('Unlink your #{provider} account to log in with linked accounts.', { provider }) : null;
};

export const getSsoErrorMessage = (errorCode: string | null | undefined): string | null => {
  if (!errorCode) return null;
  return errorCode === 'noPasswordSupplied'
    ? _t('Please enter a valid password.')
    : _t("Uh oh! We weren't able to delink your organization account. Please try again later.");
};

export const getSsoSuccessMessage = (successCode: string | null | undefined): string | null => {
  if (successCode === 'delink') return _t('Successfully delinked your SSO account.');
  return null;
};
